import React, { useState, useEffect } from 'react';
import { API_URL } from '../pages/config';

const AdminMaintenance = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Check current maintenance mode status when component mounts
    const checkMaintenanceStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/maintenance/status`);
        if (response.ok) {
          const data = await response.json();
          setMaintenanceMode(data.maintenance);
        }
      } catch (error) {
        console.error('Error checking maintenance status:', error);
      }
    };

    checkMaintenanceStatus();
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    // This is a simple authentication method
    // In a production environment, use proper authentication with backend validation
    if (password === process.env.REACT_APP_ADMIN_PASSWORD || password === 'admin123') {
      setIsAuthenticated(true);
      setPassword('');
      setMessage('');
    } else {
      setMessage('Invalid password');
    }
  };

  const toggleMaintenanceMode = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/maintenance/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          maintenance: !maintenanceMode,
          // You might want to add authentication here
          adminKey: process.env.REACT_APP_ADMIN_KEY || 'admin123',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setMaintenanceMode(data.maintenance);
        setMessage(`Maintenance mode ${data.maintenance ? 'enabled' : 'disabled'} successfully`);
        
        // Force page reload to apply changes
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setMessage('Failed to toggle maintenance mode');
      }
    } catch (error) {
      console.error('Error toggling maintenance mode:', error);
      setMessage('Error connecting to server');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full space-y-8 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-md">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
              Admin Access
            </h2>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white dark:bg-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Admin Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {message && (
              <div className="text-red-500 text-sm text-center">{message}</div>
            )}

            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Login
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full space-y-8 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-md">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Maintenance Mode Control
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Current status: 
            <span className={`ml-2 font-medium ${maintenanceMode ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
              {maintenanceMode ? 'ENABLED' : 'DISABLED'}
            </span>
          </p>
        </div>

        {message && (
          <div className={`text-sm text-center ${message.includes('error') || message.includes('failed') ? 'text-red-500' : 'text-green-500'}`}>
            {message}
          </div>
        )}

        <div className="flex justify-center">
          <button
            onClick={toggleMaintenanceMode}
            disabled={isLoading}
            className={`
              group relative flex justify-center py-3 px-6 border border-transparent text-sm font-medium rounded-md text-white
              ${maintenanceMode 
                ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' 
                : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
              }
              focus:outline-none focus:ring-2 focus:ring-offset-2
              transition-all duration-200
              ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}
            `}
          >
            {isLoading ? (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : null}
            {maintenanceMode ? 'Disable Maintenance Mode' : 'Enable Maintenance Mode'}
          </button>
        </div>
        
        <div className="mt-4 text-center">
          <button
            onClick={() => setIsAuthenticated(false)}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminMaintenance; 